import { CANONICAL, GIFI } from "../../../../../shared/helpers/constants";

// Working-capital cascades:
//   CASH_01, AR_01..AR_03, DR_01..DR_02, INV_01..INV_03, PRE_01..PRE_02, AP_01..AP_02

export const workingCapitalCascades = {
	// CASH_01 — negative cash balance
	"cash01_determine_cause": {
		type: "labelResponse",
		labels: { bank_overdraft: "Bank overdraft", timing: "Timing difference" },
	},
	"cash01_resolve": {
		type: "branch", of: "cash01_determine_cause",
		when: {
			"timing": { type: "acknowledge", detail: "Acknowledged — timing" },
			"bank_overdraft": {
				type: "proposeAjeFromPeerStep",
				peerStepId: "cash01_show_balance", amountField: "balance",
				proposal: {
					debitCanonicalCode: CANONICAL.CASH, debitDescription: "Cash", debitGifi: GIFI.CASH,
					creditCanonicalCode: CANONICAL.BANK_OVERDRAFT, creditDescription: "Bank overdraft", creditGifi: GIFI.BANK_OVERDRAFT,
					description: "Reclassify bank overdraft to current liabilities",
				},
			},
		},
	},

	// PRE_01 — expense paid but not recognized
	"pre01_determine": {
		type: "labelResponse",
		labels: {
			future_period: "Future period prepaid",
			expense_unmapped: "Expenses unmapped",
			need_aje: "Recognize expired portion",
		},
	},
	"pre01_resolve": {
		type: "branch", of: "pre01_determine",
		when: {
			"future_period": { type: "acknowledge", detail: "Acknowledged — prepaid for future period" },
			"expense_unmapped": {
				type: "pickAccount",
				keywords: ["expense", "insurance", "rent", "operating"],
				target: { canonicalCode: CANONICAL.OPERATING_EXPENSE, canonicalName: "Other expenses" },
				question: "Which account is the operating expense?",
				foundNoun: "matching",
				allDetail: "Showing all",
				blockedDetail: "No accounts available",
			},
			"need_aje": {
				type: "amountThenAje",
				amountKey: "pre01_resolve_expired_amount",
				promptDetail: "Enter expired amount",
				promptQuestion: "How much has expired?",
				inputField: { id: "expired_amount", label: "Amount ($)", input_type: "number" },
				proposal: {
					debitCanonicalCode: CANONICAL.OPERATING_EXPENSE, debitDescription: "Other expenses", debitGifi: GIFI.OPERATING_EXPENSE,
					creditCanonicalCode: CANONICAL.PREPAID_EXPENSES, creditDescription: "Prepaid expenses", creditGifi: GIFI.PREPAID_EXPENSES,
					description: "Recognize expired prepaid expense",
				},
			},
		},
	},

	// PRE_02 — negative prepaid balance
	"pre02_determine_cause": {
		type: "labelResponse",
		labels: {
			accrued_liability: "Accrued liability misclassified",
			over_amortized: "Over-amortized",
			timing: "Timing difference",
		},
	},
	"pre02_resolve": {
		type: "branch", of: "pre02_determine_cause",
		when: {
			"timing": { type: "acknowledge", detail: "Acknowledged — timing difference" },
			"accrued_liability": {
				type: "proposeAjeFromPeerStep",
				peerStepId: "pre02_show_balance", amountField: "balance",
				proposal: {
					debitCanonicalCode: CANONICAL.PREPAID_EXPENSES, debitDescription: "Prepaid expenses", debitGifi: GIFI.PREPAID_EXPENSES,
					creditCanonicalCode: CANONICAL.ACCRUED_LIABILITIES, creditDescription: "Amounts payable and accrued liabilities", creditGifi: GIFI.ACCRUED_LIABILITIES,
					description: "Reclassify accrued liability from prepaid",
				},
			},
			"over_amortized": {
				type: "proposeAjeFromPeerStep",
				peerStepId: "pre02_show_balance", amountField: "balance",
				proposal: {
					debitCanonicalCode: CANONICAL.PREPAID_EXPENSES, debitDescription: "Prepaid expenses", debitGifi: GIFI.PREPAID_EXPENSES,
					creditCanonicalCode: CANONICAL.OPERATING_EXPENSE, creditDescription: "Other expenses (reversal)", creditGifi: GIFI.OPERATING_EXPENSE,
					description: "Reverse over-amortized prepaid",
				},
			},
		},
	},

	// AP_02 — negative AP balance
	"ap02_determine_cause": {
		type: "labelResponse",
		labels: {
			vendor_overpayment: "Vendor overpayment",
			vendor_prepayment: "Vendor prepayment/deposit",
			timing: "Timing difference",
		},
	},
	"ap02_resolve": {
		type: "branch", of: "ap02_determine_cause",
		when: {
			"timing": { type: "acknowledge", detail: "Acknowledged — timing difference, will clear next period" },
			"vendor_overpayment": {
				type: "proposeAjeFromPeerStep",
				peerStepId: "ap02_show_balance", amountField: "balance",
				proposal: {
					debitCanonicalCode: CANONICAL.OTHER_RECEIVABLE, debitDescription: "Accounts receivable (vendor)", debitGifi: GIFI.TRADE_RECEIVABLE,
					creditCanonicalCode: CANONICAL.ACCOUNTS_PAYABLE, creditDescription: "Trade payables", creditGifi: GIFI.ACCOUNTS_PAYABLE,
					description: "Reclassify vendor overpayment from AP to asset",
				},
			},
			"vendor_prepayment": {
				type: "proposeAjeFromPeerStep",
				peerStepId: "ap02_show_balance", amountField: "balance",
				proposal: {
					debitCanonicalCode: CANONICAL.PREPAID_EXPENSES, debitDescription: "Prepaid expenses", debitGifi: GIFI.PREPAID_EXPENSES,
					creditCanonicalCode: CANONICAL.ACCOUNTS_PAYABLE, creditDescription: "Trade payables", creditGifi: GIFI.ACCOUNTS_PAYABLE,
					description: "Reclassify vendor prepayment from AP to asset",
				},
			},
		},
	},

	// AP_01 — payables expected but not recorded
	"ap01_determine_reason": {
		type: "labelResponse",
		labels: {
			cash_basis: "Cash-basis — no payables expected",
			all_paid: "All bills paid by year-end",
			ap_unmapped: "Accounts payable exists but is unmapped",
			need_aje: "Year-end payables need to be recorded",
		},
	},
	"ap01_resolve": {
		type: "branch", of: "ap01_determine_reason",
		when: {
			"cash_basis": { type: "acknowledge", detail: "Acknowledged — cash-basis accounting, no payables expected" },
			"all_paid": { type: "acknowledge", detail: "Acknowledged — all bills paid by year-end" },
			"ap_unmapped": {
				type: "pickAccount",
				keywords: ["payable", "accounts payable", "trade payable", "creditor", "a/p"],
				target: { canonicalCode: CANONICAL.ACCOUNTS_PAYABLE, canonicalName: "Trade payables" },
				question: "Which account should be mapped to Accounts Payable?",
				foundNoun: "account(s) that may be accounts payable",
			},
			"need_aje": {
				type: "amountThenAje",
				amountKey: "ap01_resolve_payables_amount",
				promptDetail: "Enter the year-end payables amount",
				promptQuestion: "What is the total accounts payable balance at year-end?",
				inputField: { id: "payables_amount", label: "Payables amount ($)", input_type: "number" },
				proposal: {
					debitCanonicalCode: CANONICAL.ACCRUED_EXPENSES,
					debitDescription: "Amounts payable and accrued liabilities",
					debitGifi: GIFI.OPERATING_EXPENSE,
					creditCanonicalCode: CANONICAL.ACCOUNTS_PAYABLE,
					creditDescription: "Trade payables",
					creditGifi: GIFI.ACCOUNTS_PAYABLE,
					description: "Record year-end accounts payable",
				},
			},
		},
	},

	// DR_02 — negative deferred revenue
	"dr02_determine_cause": {
		type: "labelResponse",
		labels: {
			over_recognized: "Revenue over-recognized",
			misclassified_prepaid: "Misclassified prepaid",
			timing: "Timing difference",
		},
	},
	"dr02_resolve": {
		type: "branch", of: "dr02_determine_cause",
		when: {
			"timing": { type: "acknowledge", detail: "Acknowledged — timing difference" },
			"over_recognized": {
				type: "proposeAjeFromPeerStep",
				peerStepId: "dr02_show_balance", amountField: "balance",
				proposal: {
					debitCanonicalCode: CANONICAL.TRADE_REVENUE, debitDescription: "Trade sales of goods and services (reversal)", debitGifi: GIFI.SALES,
					creditCanonicalCode: CANONICAL.DEFERRED_REVENUE, creditDescription: "Deferred income", creditGifi: GIFI.DEFERRED_REVENUE,
					description: "Reverse over-recognized revenue",
				},
			},
			"misclassified_prepaid": {
				type: "proposeAjeFromPeerStep",
				peerStepId: "dr02_show_balance", amountField: "balance",
				proposal: {
					debitCanonicalCode: CANONICAL.PREPAID_EXPENSES, debitDescription: "Prepaid expenses", debitGifi: GIFI.PREPAID_EXPENSES,
					creditCanonicalCode: CANONICAL.DEFERRED_REVENUE, creditDescription: "Deferred income", creditGifi: GIFI.DEFERRED_REVENUE,
					description: "Reclassify deferred revenue to prepaid",
				},
			},
		},
	},

	// DR_01 — large deferred revenue balance
	"dr01_determine_action": {
		type: "labelResponse",
		labels: {
			future_period: "Revenue deferred to future period",
			recognize_now: "Revenue earned — recognize now",
			advance_deposit: "Customer deposit",
		},
	},
	"dr01_resolve": {
		type: "branch", of: "dr01_determine_action",
		when: {
			"future_period": { type: "acknowledge", detail: "Acknowledged — appropriately deferred" },
			"advance_deposit": { type: "acknowledge", detail: "Acknowledged — customer deposit" },
			"recognize_now": {
				type: "amountThenAje",
				amountKey: "dr01_resolve_recognize_amount",
				promptDetail: "Enter the amount to recognize",
				promptQuestion: "How much deferred revenue should be recognized?",
				inputField: { id: "recognize_amount", label: "Amount ($)", input_type: "number" },
				proposal: {
					debitCanonicalCode: CANONICAL.DEFERRED_REVENUE,
					debitDescription: "Deferred income",
					debitGifi: GIFI.DEFERRED_REVENUE,
					creditCanonicalCode: CANONICAL.TRADE_REVENUE,
					creditDescription: "Trade sales of goods and services",
					creditGifi: GIFI.SALES,
					description: "Recognize deferred revenue earned in current year",
				},
			},
		},
	},

	// AR_03 — negative AR balance
	"ar03_determine_cause": {
		type: "labelResponse",
		labels: {
			customer_overpayment: "Customer overpayment",
			advance_deposit: "Advance deposit",
			credit_note: "Credit notes posted to AR",
			timing: "Timing difference",
		},
	},
	"ar03_resolve": {
		type: "branch", of: "ar03_determine_cause",
		when: {
			"timing": { type: "acknowledge", detail: "Acknowledged — timing difference, will clear in next period" },
			"customer_overpayment": {
				type: "proposeAjeFromPeerStep",
				peerStepId: "ar03_show_balance", amountField: "balance",
				proposal: {
					debitCanonicalCode: CANONICAL.TRADE_RECEIVABLE, debitDescription: "Accounts receivable", debitGifi: GIFI.TRADE_RECEIVABLE,
					creditCanonicalCode: CANONICAL.CUSTOMER_DEPOSITS, creditDescription: "Deposits received", creditGifi: GIFI.CUSTOMER_DEPOSITS,
					description: "Reclassify customer overpayment from AR",
				},
			},
			"advance_deposit": {
				type: "proposeAjeFromPeerStep",
				peerStepId: "ar03_show_balance", amountField: "balance",
				proposal: {
					debitCanonicalCode: CANONICAL.TRADE_RECEIVABLE, debitDescription: "Accounts receivable", debitGifi: GIFI.TRADE_RECEIVABLE,
					creditCanonicalCode: CANONICAL.DEFERRED_REVENUE, creditDescription: "Deferred income", creditGifi: GIFI.DEFERRED_REVENUE,
					description: "Reclassify advance deposit from AR",
				},
			},
			"credit_note": {
				type: "proposeAjeFromPeerStep",
				peerStepId: "ar03_show_balance", amountField: "balance",
				proposal: {
					debitCanonicalCode: CANONICAL.TRADE_RECEIVABLE, debitDescription: "Accounts receivable", debitGifi: GIFI.TRADE_RECEIVABLE,
					creditCanonicalCode: CANONICAL.SALES_RETURNS, creditDescription: "Trade sales of goods and services (returns)", creditGifi: GIFI.SALES_RETURNS,
					description: "Reclassify credit note from AR",
				},
			},
		},
	},

	// AR_01 — AR exists but no Trade Revenue
	"ar01_determine_nature": {
		type: "labelResponse",
		labels: {
			shareholder_loan: "Shareholder loan receivable",
			tax_refund: "Tax refund receivable",
			non_trade_other: "Other non-trade receivable",
			revenue_unmapped: "Trade revenue exists but is unmapped",
		},
	},
	"ar01_resolve": {
		type: "branch", of: "ar01_determine_nature",
		when: {
			"shareholder_loan": { type: "acknowledge", detail: "Acknowledged — shareholder loan, not trade AR" },
			"tax_refund": { type: "acknowledge", detail: "Acknowledged — tax refund receivable" },
			"non_trade_other": { type: "acknowledge", detail: "Acknowledged — non-trade receivable" },
			"revenue_unmapped": {
				type: "pickAccount",
				keywords: ["revenue", "sales", "income", "fees", "service"],
				target: { canonicalCode: CANONICAL.TRADE_REVENUE, canonicalName: "Trade sales of goods and services" },
				question: "Which account should be mapped to Trade Revenue?",
				foundNoun: "account(s) that may be trade revenue",
			},
		},
	},

	// AR_02 — revenue exists but no AR
	"ar02_determine_reason": {
		type: "labelResponse",
		labels: {
			cash_basis: "Cash-basis — revenue collected immediately",
			ar_unmapped: "Accounts receivable exists but is unmapped",
			prepaid_model: "Revenue is prepaid — customer deposits apply",
		},
	},
	"ar02_resolve": {
		type: "branch", of: "ar02_determine_reason",
		when: {
			"cash_basis": { type: "acknowledge", detail: "Acknowledged — cash-basis business, no receivables expected" },
			"prepaid_model": { type: "acknowledge", detail: "Acknowledged — prepaid/deposit model, receivables not applicable" },
			"ar_unmapped": {
				type: "pickAccount",
				keywords: ["receivable", "accounts receivable", "trade receivable", "customer", "a/r"],
				target: { canonicalCode: CANONICAL.TRADE_RECEIVABLE, canonicalName: "Accounts receivable" },
				question: "Which account should be mapped to Trade Receivables?",
				foundNoun: "account(s) that may be accounts receivable",
			},
		},
	},

	// INV_03 — negative inventory balance
	"inv03_determine_cause": {
		type: "labelResponse",
		labels: {
			cogs_mispost: "COGS misposted to inventory",
			adjustment_error: "Adjustment reversed or double-posted",
			write_down: "Written down below zero",
		},
	},
	"inv03_resolve": {
		type: "branch", of: "inv03_determine_cause",
		when: {
			"cogs_mispost": {
				type: "proposeAjeFromPeerStep",
				peerStepId: "inv03_show_balance", amountField: "balance",
				proposal: {
					debitCanonicalCode: CANONICAL.COGS, debitDescription: "Cost of sales", debitGifi: GIFI.COST_OF_GOODS_SOLD,
					creditCanonicalCode: CANONICAL.INVENTORY, creditDescription: "Inventory of goods for sale", creditGifi: GIFI.INVENTORY,
					description: "Reclassify COGS from inventory",
				},
			},
			"adjustment_error,write_down": {
				type: "proposeAjeFromPeerStep",
				peerStepId: "inv03_show_balance", amountField: "balance",
				proposal: {
					debitCanonicalCode: CANONICAL.INVENTORY, debitDescription: "Inventory of goods for sale", debitGifi: GIFI.INVENTORY,
					creditCanonicalCode: CANONICAL.COGS, creditDescription: "Cost of sales (correction)", creditGifi: GIFI.COST_OF_GOODS_SOLD,
					description: "Correct negative inventory balance",
				},
			},
		},
	},

	// INV_02 — COGS but no Inventory
	"inv02_determine_reason": {
		type: "labelResponse",
		labels: {
			service_business: "Service business — COGS is direct costs",
			consignment_dropship: "Consignment/drop-ship model",
			inventory_unmapped: "Inventory exists but is unmapped",
		},
	},
	"inv02_resolve": {
		type: "branch", of: "inv02_determine_reason",
		when: {
			"service_business": { type: "acknowledge", detail: "Acknowledged — service business, COGS without inventory is expected" },
			"consignment_dropship": { type: "acknowledge", detail: "Acknowledged — consignment/drop-ship, no inventory held" },
			"inventory_unmapped": {
				type: "pickAccount",
				keywords: ["inventory", "stock", "goods", "merchandise", "supplies", "raw material"],
				target: { canonicalCode: CANONICAL.INVENTORY, canonicalName: "Inventory of goods for sale" },
				question: "Which account should be mapped to Inventory?",
			},
		},
	},

	// INV_01 — Inventory exists but no COGS account
	"inv01_determine_resolution": {
		type: "labelResponse",
		labels: {
			sells_goods: "Business sells physical goods — COGS account needs to be mapped",
			supplies_only: "Inventory is supplies only — no COGS expected",
		},
	},
	"inv01_apply_fix": {
		type: "branch", of: "inv01_determine_resolution",
		when: {
			"supplies_only": { type: "acknowledge", detail: "Acknowledged — inventory is supplies only, no COGS expected" },
			"sells_goods": {
				type: "pickAccount",
				responseKey: "inv01_apply_fix",
				keywords: ["cost of goods", "cost of sales", "cogs", "purchases", "materials", "cost of product"],
				target: { canonicalCode: CANONICAL.COGS, canonicalName: "Cost of sales" },
				question: "Which account should be mapped to Cost of Goods Sold?",
				foundNoun: "account(s) that may be cost of goods sold",
				allDetail: "No obvious matches — showing all accounts",
			},
		},
	},
};
